import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { RiskBadge } from "../components/clinical";
import { PlusCircle, FolderClock, BookOpen, ArrowRight, Activity, Hourglass, PlayCircle } from "lucide-react";
import { PendingRow } from "./PendingCases";

export default function Dashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
  }, []);

  const pending = cases.filter((c) => c.status === "pending");
  const counts = cases.reduce((a, c) => { const s = c.decision?.overall_status; a[s] = (a[s] || 0) + 1; return a; }, {});

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Welcome, {user?.name?.split(" ")[0] || "Doctor"}</h1>
      <p className="text-slate-500 mt-1">Run a new medical risk assessment or review your saved cases.</p>

      <Link to="/app/tutorial" data-testid="dashboard-tutorial-card"
        className="mt-6 flex items-center gap-4 rounded-xl border border-sky-200 bg-sky-50/70 p-3 pr-5 hover:bg-sky-50 transition-colors group">
        <span className="relative shrink-0 w-28 sm:w-36 aspect-video rounded-lg overflow-hidden bg-slate-200">
          <img src="/media/tutorial-poster.jpg" alt="DentiCision tutorial" className="w-full h-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-[#0B2545]/30"><PlayCircle className="w-9 h-9 text-white transition-transform group-hover:scale-110" /></span>
        </span>
        <span>
          <span className="block font-semibold text-[#0B2545]">{cases.length < 3 ? "New to DentiCision? Watch the 2-minute tutorial" : "How to use DentiCision — video tutorial"}</span>
          <span className="block text-sm text-slate-600">From the medical history to a clear, referenced decision.</span>
        </span>
      </Link>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {[
          { label: "Total cases", value: cases.length, color: "text-slate-900" },
          { label: "Modify / caution", value: counts.yellow || 0, color: "text-amber-600" },
          { label: "Specialist approval / defer", value: (counts.red || 0) + (counts.orange || 0), color: "text-red-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className={`font-heading text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Link to="/app/new" className="group bg-gradient-to-br from-sky-600 to-sky-700 rounded-xl p-6 text-white shadow-sm">
          <PlusCircle className="w-7 h-7" />
          <h3 className="font-heading text-lg font-semibold mt-3">New Evaluation</h3>
          <p className="text-sky-100 text-sm mt-1">Assess a patient in a few clicks</p>
          <span className="inline-flex items-center text-sm font-medium mt-3 group-hover:gap-2 gap-1 transition-all">Start <ArrowRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/app/cases" className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:border-sky-300 transition-colors">
          <FolderClock className="w-7 h-7 text-slate-700" />
          <h3 className="font-heading text-lg font-semibold text-slate-900 mt-3">Case History</h3>
          <p className="text-slate-500 text-sm mt-1">Review and reprint past cases</p>
        </Link>
        <Link to="/app/knowledge" className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:border-sky-300 transition-colors">
          <BookOpen className="w-7 h-7 text-slate-700" />
          <h3 className="font-heading text-lg font-semibold text-slate-900 mt-3">Content Base</h3>
          <p className="text-slate-500 text-sm mt-1">Browse referenced clinical modules</p>
        </Link>
      </div>

      {pending.length > 0 && (
        <div className="mt-8" data-testid="dashboard-pending">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2"><Hourglass className="w-5 h-5 text-amber-600" /> Pending cases <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" data-testid="dashboard-pending-count">{pending.length}</span></h2>
            <Link to="/app/pending" className="text-sm text-sky-600 hover:underline">View all</Link>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 divide-y divide-slate-100">
            {pending.slice(0, 5).map((c) => <PendingRow key={c.id} c={c} />)}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Recent cases</h2>
          <Link to="/app/cases" className="text-sm text-sky-600 hover:underline">View all</Link>
        </div>
        {cases.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center">
            <Activity className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3">No cases yet. Run your first evaluation.</p>
            <Link to="/app/new"><Button className="mt-4 bg-slate-900 hover:bg-slate-800">New Evaluation</Button></Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {cases.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{c.patient?.name || "Unnamed patient"}</p>
                  <p className="text-sm text-slate-500 truncate">{c.procedure_name} · {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <RiskBadge status={c.decision?.overall_status || "green"} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
